import { openDB } from 'idb';
import { v4 as uuidv4 } from 'uuid';
import { db as firestoreDB } from '../firebaseConfig.ts';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { CONFIG } from '../config';
import { getFirestoreDocUrl } from './firestoreApi';
// import { loadAndMigrateResult } from './firebaseService';
import { validateData, deepClean } from './runtimeValidation.ts';

export interface SessionData {
  id?: string;
  step?: string;
  currentQuestionIndex?: number;
  demographics?: Record<string, any>;
  questions?: any[];
  answers?: any[];
  startTime?: string;
  completed?: boolean;
}

export interface TestResult {
  [key: string]: any;
}

const DB_NAME = 'PsycoDB';
const SESSIONS_STORE = 'sessions';
// Имя object store для локального кэша результатов в IndexedDB (НЕ Firestore)
const RESULTS_LOCAL_STORE = 'results';

let indexedDbAvailable = true;
let indexedDbErrorLogged = false;

// Initialize the database
export const initDB = async () => {
  if (!indexedDbAvailable) return null;
  try {
    return await openDB(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(SESSIONS_STORE)) {
          db.createObjectStore(SESSIONS_STORE, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(RESULTS_LOCAL_STORE)) {
          db.createObjectStore(RESULTS_LOCAL_STORE, { keyPath: 'id' });
        }
      },
    });
  } catch (e) {
    indexedDbAvailable = false;
    if (!indexedDbErrorLogged) {
      console.warn('[IndexedDB] Недоступен, все операции будут выполняться только через Firestore:', e && e.message);
      indexedDbErrorLogged = true;
    }
    return null;
  }
};

/**
 * Saves the current test session progress to IndexedDB.
 * @param {object} sessionData - The data to save (e.g., answers, current question index).
 * @returns {string} The session ID.
 */
export const saveSession = async (sessionData: SessionData) => {
  const cleanSession = deepClean(sessionData);
  let db = await initDB();
  if (!db) return null;
  // Если есть схема для сессии, можно добавить validateData('session.schema.json', cleanSession);
  const sessionId = cleanSession.id || uuidv4();
  const dataToSave = { ...cleanSession, id: sessionId, timestamp: new Date() };
  try {
    if (db) await db.put(SESSIONS_STORE, dataToSave);
  } catch (e) {
    if (!indexedDbErrorLogged) {
      console.warn('[IndexedDB] Ошибка при put:', e && e.message);
      indexedDbErrorLogged = true;
    }
    return null;
  }
  // Keep a reference in localStorage for easy access to the current session ID
  localStorage.setItem('psyco_current_session_id', sessionId);
  return sessionId;
};

/**
 * Loads a session from IndexedDB by its ID.
 * @param {string} sessionId - The ID of the session to load.
 * @returns {object|null} The session data or null if not found.
 */
export const loadSession = async (sessionId?: string) => {
  if (!sessionId) return null;
  const db = await initDB();
  try {
    if (db) return await db.get(SESSIONS_STORE, sessionId);
  } catch (error) {
    console.error('Failed to load session from IndexedDB:', error);
    return null;
  }
};

/**
 * Gets the ID of the last active session from localStorage.
 */
export const getCurrentSessionId = () => {
    return localStorage.getItem('psyco_current_session_id');
}

/**
 * Clears a session from IndexedDB and localStorage.
 * @param {string} sessionId - The ID of the session to clear.
 */
export const clearSession = async (sessionId?: string) => {
  if (!sessionId) return;
  const db = await initDB();
  if (db) await db.delete(SESSIONS_STORE, sessionId);
  localStorage.removeItem('psyco_current_session_id');
};

/**
 * @param {import('../types/result_test.schema').ResultTestSchema} resultsData - The final results object from calculateResults.
 * @returns {Promise<string>} The result ID.
 */
export const saveResults = async (resultsData: TestResult, answers: any[]) => {
  const cleanResults = deepClean(resultsData);
  try {
    validateData('result_test.schema.json', cleanResults);
  } catch (e) {
    console.error('Ошибка валидации результата перед сохранением:', e.message);
    throw e;
  }
  const db = await initDB();
  // Use existing ID if present, otherwise generate a new one
  const id = cleanResults.id || uuidv4();
  const dataToSave = {
    ...cleanResults,
    id,
    answers,
    timestamp: new Date(),
    synced: false,
  };
  
  // --- Логируем имя коллекции и resultId ---
  const collectionName = CONFIG.COLLECTIONS.RESULTS;
  if (typeof window !== 'undefined' && (window as any).logPsycoDebug) {
    (window as any).logPsycoDebug({ step: 'saveResults-init', collection: collectionName, resultId: id, dataToSave });
  } else {
    console.log(`[saveResults] Сохраняю результат в коллекцию: ${collectionName}, resultId: ${id}`);
  }

  // Save to IndexedDB first
  try {
    if (db) await db.put(RESULTS_LOCAL_STORE, dataToSave);
    if (typeof window !== 'undefined' && (window as any).logPsycoDebug) {
      (window as any).logPsycoDebug({ step: 'saveResults-indexeddb-ok', resultId: id });
    }
  } catch (e) {
    if (typeof window !== 'undefined' && (window as any).logPsycoDebug) {
      (window as any).logPsycoDebug({ step: 'saveResults-indexeddb-fail', resultId: id, error: e && e.message });
    }
    throw e;
  }

  // Attempt to save to Firebase
  try {
    await setDoc(doc(firestoreDB, collectionName, id), dataToSave, { merge: true });
    // If successful, mark as synced in IndexedDB
    if (db) await db.put(RESULTS_LOCAL_STORE, { ...dataToSave, synced: true });
    if (typeof window !== 'undefined' && (window as any).logPsycoDebug) {
      (window as any).logPsycoDebug({ step: 'saveResults-firebase-ok', resultId: id });
    }
    console.log(`Results ${id} saved to Firebase and marked as synced.`);
  } catch (error) {
    if (typeof window !== 'undefined' && (window as any).logPsycoDebug) {
      (window as any).logPsycoDebug({ step: 'saveResults-firebase-fail', resultId: id, error: error && error.message });
    }
    console.error(`Failed to save results ${id} to Firebase. They are saved locally.`, error);
  }

  return id;
};

// --- Firestore fields to plain object ---
export function firestoreFieldsToObject(fields: any): any {
  if (fields === null || fields === undefined) return null;
  if (Array.isArray(fields)) return fields.map(firestoreFieldsToObject);
  if (typeof fields !== 'object') return fields;

  // --- Примитивные Firestore-обёртки ---
  if (fields.stringValue !== undefined) return fields.stringValue;
  if (fields.integerValue !== undefined) return Number(fields.integerValue);
  if (fields.doubleValue !== undefined) return Number(fields.doubleValue);
  if (fields.booleanValue !== undefined) return fields.booleanValue;
  if (fields.arrayValue !== undefined) {
    const arr = fields.arrayValue.values;
    return Array.isArray(arr) ? arr.map(firestoreFieldsToObject) : [];
  }
  if (fields.mapValue !== undefined) {
    const mapFields = fields.mapValue.fields;
    return mapFields ? firestoreFieldsToObject(mapFields) : {};
  }

  // --- Обычный объект Firestore fields ---
  const obj: any = {};
  for (const [key, value] of Object.entries(fields)) {
    obj[key] = firestoreFieldsToObject(value);
  }
  return obj;
}

async function getDB() {
  return initDB();
}

export async function loadResults(resultId: string, options: { skipIndexedDB?: boolean; debugLog?: boolean } = {}) {
  // Ensure resultId is a string
  resultId = String(resultId).trim();
  if (!resultId) {
    throw new Error('Invalid resultId provided to loadResults');
  }
  const { skipIndexedDB = false, debugLog = false } = options;
  const db = await getDB();
  const isE2EEnv = typeof window !== 'undefined' && (window as any).PLAYWRIGHT_E2E;
  // Для e2e всегда skipIndexedDB
  const useIndexedDB = !isE2EEnv && !skipIndexedDB;
  if (debugLog) console.log('[loadResults][DEBUG] called with resultId=' + resultId + ', skipIndexedDB=' + skipIndexedDB);
  const isProd = CONFIG.ENV.isProduction;
  const isE2E = CONFIG.ENV.isE2E;

  // --- Always try to fetch from Firestore in production ---
  if (isE2E || isProd) {
    try {
      const collectionName = CONFIG.COLLECTIONS.RESULTS;
      if (debugLog) {
        console.log('[PROD_DEBUG][loadResults] Fetching from Firestore:', { 
          collectionName, 
          resultId,
          isProduction: isProd,
          isE2E: isE2E,
          timestamp: new Date().toISOString()
        });
      }
      
      const url = getFirestoreDocUrl(collectionName, resultId);
      if (debugLog) {
        console.log('[PROD_DEBUG][loadResults] Firestore URL:', url);
      }
      
      // Get the current user's ID token
      const auth = getAuth();
      const fetchOptions: RequestInit = {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store' as RequestCache,
        credentials: 'include' as const
      };

      // Add auth token if user is authenticated
      if (auth.currentUser) {
        try {
          const token = await auth.currentUser.getIdToken();
          fetchOptions.headers = {
            ...fetchOptions.headers,
            'Authorization': `Bearer ${token}`
          };
          console.log('[PROD_DEBUG][loadResults] Using authenticated request');
        } catch (authError) {
          console.error('[PROD_DEBUG][loadResults] Error getting auth token:', authError);
        }
      } else {
        console.log('[PROD_DEBUG][loadResults] No authenticated user, making public request');
      }

      try {
        const startTime = Date.now();
        const resp = await fetch(url, fetchOptions);
        const responseTime = Date.now() - startTime;
        
        console.log('[PROD_DEBUG][loadResults] Response received:', {
          status: resp.status,
          statusText: resp.statusText,
          responseTime: `${responseTime}ms`,
          headers: Object.fromEntries(resp.headers.entries()),
          url: resp.url,
          redirected: resp.redirected,
          type: resp.type
        });

        if (!resp.ok) {
          // Log response body for error cases
          const errorText = await resp.text();
          console.error('[PROD_DEBUG][loadResults] Error response body:', errorText);
          console.error('[PROD_DEBUG][loadResults] Request details:', {
            method: 'GET',
            url: url,
            status: resp.status,
            statusText: resp.statusText,
            error: errorText
          });
          
          // Try to get more details from error response
          try {
            const errorJson = JSON.parse(errorText);
            console.error('[loadResults][ERROR] Firestore error details:', errorJson);
          } catch (e) {
            // Not a JSON response
          }
          
          // Fall through to IndexedDB check
        } else {
          const responseData = await resp.json();
          console.log('[PROD_DEBUG][loadResults] Firestore response data:', responseData);
          
          if (!responseData.fields) {
            console.error('[loadResults][ERROR] Firestore doc has no fields:', {
              documentExists: !!responseData,
              documentKeys: responseData ? Object.keys(responseData) : 'no data',
              fullResponse: responseData
            });
            // Fall through to IndexedDB check
          } else {
            // Convert Firestore fields to plain object
            const result = firestoreFieldsToObject(responseData.fields);
            if (debugLog) console.log('[loadResults][DEBUG] Firestore doc parsed:', result);
            
            // Save to IndexedDB for offline access
            if (db) {
              try {
                await db.put(RESULTS_LOCAL_STORE, result);
              } catch (e) {
                console.warn('[loadResults] Failed to cache result in IndexedDB:', e);
              }
            }
            
            return result;
          }
        }
      } catch (e) {
        console.error('[loadResults][ERROR] Exception in Firestore fetch:', e);
        // Fall through to IndexedDB check
      }
    } catch (e) {
      console.error('[loadResults][ERROR] Unexpected error in Firestore fetch:', e);
      // Fall through to IndexedDB check
    }
  }

  // --- Fallback to IndexedDB if Firestore fetch fails ---
  if (useIndexedDB && db) {
    // 1. Try to get from IndexedDB
    if (debugLog) console.log('[loadResults] trying IndexedDB...');
    try {
      const localResult = await db.get(RESULTS_LOCAL_STORE, resultId);
      if (localResult) {
        if (debugLog) console.log('[loadResults] found in IndexedDB:', localResult);
        if (typeof window !== 'undefined' && (window as any).logPsycoDebug) {
          (window as any).logPsycoDebug({ step: 'loadResults-after-IndexedDB', resultId, found: true });
        }
        // --- Миграция и валидация ---
        try {
          validateData('result_test.schema.json', localResult);
          return localResult;
        } catch (e) {
          // Если невалиден — удаляем из IndexedDB и пробуем снова
          try { if (db) await db.delete(RESULTS_LOCAL_STORE, resultId); } catch (delErr) { console.warn('Не удалось удалить битый результат из IndexedDB:', delErr); }
          // Пробуем legacy-валидацию и миграцию
          try {
            // Удалить все импорты и вызовы migrateResultToCurrentSchema и validateLegacyData.
            // Пробуем legacy-валидацию и миграцию
            // const migrated = await loadAndMigrateResult(localResult);
            // validateData('result_test.schema.json', migrated);
            // try { if (db) await db.put(RESULTS_LOCAL_STORE, migrated); } catch (putErr) { console.warn('Не удалось сохранить результат в IndexedDB:', putErr); }
            // return migrated;
            throw new Error('Миграция legacy-результатов не поддерживается: отсутствует функция loadAndMigrateResult');
          } catch (legacyErr) {
            console.error('Миграция не удалась:', legacyErr.message);
            // После удаления — пробуем снова (форсируем загрузку из Firestore)
            return await loadResults(resultId);
          }
        }
      }
    } catch (error) {
      console.error('[loadResults] Error accessing IndexedDB:', error);
    }
  }

  // --- Firestore SDK  // 2. Try to get from Firestore using SDK
  let lastError = null;
  try {
    if (debugLog) console.log(`[loadResults][DEBUG] Trying Firestore SDK for ${resultId}...`);
    const collectionName = CONFIG.COLLECTIONS.RESULTS; // Define collectionName at the start of the SDK section
    const docRef = doc(firestoreDB, collectionName, resultId);
    const docSnap = await getDoc(docRef);

    // Debug log the document data if it exists
    if (docSnap.exists()) {
      if (debugLog) console.log('[loadResults] Document found in Firestore');
      const docData = docSnap.data();
      if (debugLog) console.log('  - Document data:', JSON.stringify(docData, null, 2));
      
      // Process and return the document data
      const processedData = { ...docData, id: docSnap.id };
      
      // Cache in IndexedDB for future use
      if (db) {
        try {
          await db.put(RESULTS_LOCAL_STORE, processedData);
          if (debugLog) console.log('[loadResults] Cached result in IndexedDB');
        } catch (putErr) {
          console.warn('Failed to save result to IndexedDB:', putErr);
        }
      }
      
      if (typeof window !== 'undefined' && (window as any).logPsycoDebug) {
        (window as any).logPsycoDebug({ step: 'loadResults-after-Firestore', resultId, found: true });
      }
      return processedData;
    } else {
       if (debugLog) console.log('[loadResults][DEBUG] Document does not exist in Firestore via SDK.');
    }
  } catch (error: any) {
    console.error(`[Firestore SDK] Error fetching document ${resultId}.`, error);
    lastError = error;
  }

  // 3. Fallback: Firestore REST API with retries (but not in E2E mode)
  if (!isE2E) {
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const restCollection = CONFIG.COLLECTIONS.RESULTS; // Use the collection from config
        const apiUrl = getFirestoreDocUrl(restCollection, resultId);
        if (debugLog) console.log(`[loadResults][REST][attempt ${attempt}] fetch from ${apiUrl}`);
        const res = await fetch(apiUrl);
        if (typeof window !== 'undefined' && (window as any).logPsycoDebug) {
          (window as any).logPsycoDebug({ step: 'loadResults-fetch-status', resultId, attempt, status: res.status, ok: res.ok });
        }
        if (!res.ok) throw new Error('REST API fetch failed: ' + res.status);
        const json = await res.json();
        if (debugLog) console.log(`[loadResults][REST][attempt ${attempt}] fetch result:`, json);
        const fields = json.fields || {};
        const resultObj: any = { id: resultId };
        for (const [k, v] of Object.entries(fields)) {
          const value: any = v;
          if (value.stringValue !== undefined) resultObj[k] = value.stringValue;
          else if (value.integerValue !== undefined) resultObj[k] = Number(value.integerValue);
          else if (value.doubleValue !== undefined) resultObj[k] = Number(value.doubleValue);
          else if (value.mapValue !== undefined) resultObj[k] = value.mapValue.fields;
          else if (value.arrayValue !== undefined) {
            if (Array.isArray(value.arrayValue.values)) {
              resultObj[k] = value.arrayValue.values.map(val => {
                const vval: any = val;
                if (vval.mapValue && vval.mapValue.fields) {
                  const obj: any = {};
                  for (const [kk, vv] of Object.entries(vval.mapValue.fields)) {
                    const vvv: any = vv;
                    if (vvv.stringValue !== undefined) obj[kk] = vvv.stringValue;
                    else if (vvv.integerValue !== undefined) obj[kk] = Number(vvv.integerValue);
                    else if (vvv.doubleValue !== undefined) obj[kk] = Number(vvv.doubleValue);
                    else if (vvv.mapValue !== undefined) obj[kk] = vvv.mapValue.fields;
                    else if (vvv.arrayValue !== undefined) obj[kk] = vvv.arrayValue.values;
                    else obj[kk] = vvv;
                  }
                  return obj;
                }
                return vval;
              });
            } else {
              resultObj[k] = value.arrayValue.values;
            }
            if (k === 'responses' && resultObj[k] && !Array.isArray(resultObj[k])) {
              resultObj[k] = Object.entries(resultObj[k]).map(([id, answer]) => ({ id, answer }));
            }
          } else resultObj[k] = value;
        }
        // sessionQuestions уже приведён к массиву, если это нужно, на этапе firestoreFieldsToObject
        // --- Миграция и валидация ---
        try {
          validateData('result_test.schema.json', resultObj);
          if (db) {
            try { if (db) await db.put(RESULTS_LOCAL_STORE, resultObj); } catch (putErr) { console.warn('Не удалось сохранить результат в IndexedDB:', putErr); }
          }
          if (typeof window !== 'undefined' && (window as any).logPsycoDebug) {
            (window as any).logPsycoDebug({ step: 'loadResults-after-IndexedDB', resultId, found: true });
          }
          return resultObj;
        } catch (e) {
          // Пробуем legacy-валидацию и миграцию
          try {
            // Удалить все импорты и вызовы migrateResultToCurrentSchema и validateLegacyData.
            // Пробуем legacy-валидацию и миграцию
            // const migrated = await loadAndMigrateResult(resultObj);
            // validateData('result_test.schema.json', migrated);
            // if (db) {
            //   try { if (db) await db.put(RESULTS_LOCAL_STORE, migrated); } catch (putErr) { console.warn('Не удалось сохранить результат в IndexedDB:', putErr); }
            // }
            // if (typeof window !== 'undefined' && (window as any).logPsycoDebug) {
            //   (window as any).logPsycoDebug({ step: 'loadResults-after-IndexedDB', resultId, found: true });
            // }
            // return migrated;
            throw new Error('Миграция legacy-результатов не поддерживается: отсутствует функция loadAndMigrateResult');
          } catch (legacyErr) {
            console.error('Миграция не удалась:', legacyErr.message);
            throw legacyErr;
          }
        }
      } catch (e) {
        lastError = e;
        console.error(`[loadResults][REST][attempt ${attempt}] Error:`, e);
      }
      await new Promise(res => setTimeout(res, 1000 * attempt));
    }
  }

  // Если не нашли — логируем и возвращаем null
  if (CONFIG.DEBUG.IS_E2E) {
    // --- Логируем все id в results через REST API ---
    try {
        const collectionName = CONFIG.COLLECTIONS.RESULTS;
        fetch(getFirestoreDocUrl(collectionName, resultId))
          .then(r => r.json())
          .then(json => {
            const ids = (json.documents || []).map(doc => doc.name.split('/').pop());
            console.error(`[loadResults][DEBUG] All ids in ${collectionName}:`, ids);
          });
      } catch (e) {
        console.error(`[loadResults][VERY_DEBUG] Firestore SDK threw a critical error for ${resultId}:`, JSON.stringify(e, Object.getOwnPropertyNames(e)));
      }
    }

    console.error(`[loadResults] Failed to find result. resultId=${resultId} collection=${CONFIG.COLLECTIONS.RESULTS}`, lastError);
    return null;
}